<template>
    <div>
        <page-header>Single Sign-On</page-header>

        <page-loading v-if="loading" />

        <div v-else-if="config" class="flex flex-col gap-6">
            <vx-alert
                v-if="!config.connection_enabled || config.verified_domains_count === 0"
                color="info"
                :closeable="false"
                shadow
                data-test="setup-hint"
            >
                Verify the email domains your team logs in with, then connect your identity provider. Members whose
                email is in a verified domain can log in with SSO once the connection is enabled.
            </vx-alert>

            <sso-verified-domains :teamId="props.teamId" :requireSso="props.requireSso" @changed="loadConfig" />

            <sso-connection :teamId="props.teamId" :config="config" @changed="onConnectionChanged" />

            <sso-scim ref="scim" :teamId="props.teamId" />

            <sso-require-sso
                :teamId="props.teamId"
                :ownsTeam="props.ownsTeam"
                :config="config"
                :requireSso="props.requireSso"
                @updated="onConfigUpdated"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { VxAlert } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { ssoClient } from '~/services';
import type { SsoConfig } from '~/services/ssoClient.types';
import SsoConnection from './SsoConnection.vue';
import SsoRequireSso from './SsoRequireSso.vue';
import SsoScim from './SsoScim.vue';
import SsoVerifiedDomains from './SsoVerifiedDomains.vue';

const props = defineProps<{
    teamId: number;
    ownsTeam: boolean;
    requireSso: boolean;
}>();

const emit = defineEmits<{
    requireSsoChanged: [value: boolean];
}>();

const toasted = useToasted();

const config = ref<SsoConfig | null>(null);
const loading = ref(true);
const scim = ref<InstanceType<typeof SsoScim> | null>(null);

const loadConfig = async () => {
    try {
        const { data } = await ssoClient.getConfig(props.teamId);
        config.value = data;
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

const onConnectionChanged = async () => {
    await loadConfig();
    scim.value?.load();
};

const onConfigUpdated = (updated: SsoConfig) => {
    config.value = updated;
    emit('requireSsoChanged', updated.require_sso);
};

onMounted(loadConfig);
</script>
